import React, { useState, useEffect, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';

// Eagerly loaded — needed immediately on first paint
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import ChangePasswordModal from './components/ChangePasswordModal';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Code-split — loaded only when the user navigates to that route
const Employees = lazy(() => import('./components/Employees'));
const Attendance = lazy(() => import('./components/Attendance'));
const Payroll    = lazy(() => import('./components/Payroll'));
const Rigs       = lazy(() => import('./components/Rigs'));
const Settings   = lazy(() => import('./components/Settings'));
const SystemSettings = lazy(() => import('./components/SystemSettings'));

// Lightweight page-level loading fallback
// Upgraded skeleton loader for better LCP during route transitions
const PageLoader = () => (
  <div className="space-y-8 animate-pulse p-4 lg:p-8">
    <div className="flex items-center gap-4 mb-8">
      <div className="w-16 h-16 bg-gray-200 rounded-2xl" />
      <div className="space-y-2">
        <div className="h-8 w-48 bg-gray-200 rounded-lg" />
        <div className="h-4 w-32 bg-gray-100 rounded" />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="h-32 bg-gray-200 rounded-2xl shadow-sm" />
      ))}
    </div>
    <div className="h-64 bg-gray-100 rounded-3xl" />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const formatError = (error) => {
  if (!error) return 'An unknown error occurred';
  if (typeof error === 'string') return error;

  // Handle Axios response errors
  const data = error.response?.data;
  if (data) {
    // FastAPI format: { detail: [{loc, msg, type}] } or { detail: "string" }
    if (data.detail) {
      if (Array.isArray(data.detail)) {
        return data.detail.map(d => d.msg || JSON.stringify(d)).join(', ');
      }
      if (typeof data.detail === 'object') {
        return data.detail.msg || JSON.stringify(data.detail);
      }
      return data.detail;
    }

    // Laravel validation format: { message: "...", errors: { field: ["msg"] } }
    if (data.errors) {
      return Object.values(data.errors).flat().join(', ');
    }

    if (data.message) return data.message;
  }

  if (error.message) return error.message;
  return JSON.stringify(error);
};

// Add auth token to all axios requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle errors globally, including blobs
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on the login page or attempting to login
      const isLoginRequest = error.config.url.includes('/auth/login') || error.config.url.includes('/auth/verify-otp');

      if (!isLoginRequest) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('lastActiveTime');
        window.location.href = '/';
      }
    }

    // Convert Blob error responses (like 404s on PDF/Excel downloads) to JSON
    if (
      error.response &&
      error.response.data instanceof Blob &&
      error.response.data.type === 'application/json'
    ) {
      try {
        const textData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsText(error.response.data);
        });
        error.response.data = JSON.parse(textData);
      } catch (e) {
        // Silently ignore parsing errors
      }
    }

    return Promise.reject(error);
  }
);

// Retry helper — automatically retries on transient network errors (ERR_NETWORK_CHANGED etc.)
const axiosWithRetry = async (config, retries = 3, delay = 500) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await axios(config);
    } catch (err) {
      const isTransient = !err.response; // No response = network error
      if (isTransient && attempt < retries) {
        await new Promise(r => setTimeout(r, delay * attempt)); // 500ms, 1000ms, 1500ms
        continue;
      }
      throw err; // Re-throw on final attempt or non-network errors
    }
  }
};

import {
  LayoutDashboard,
  Settings as SettingsIcon,
  Users,
  FileText,
  ClipboardCheck,
  Factory,
  LogOut,
  Menu,
  X,
  Calculator
} from 'lucide-react';

const Navigation = ({ onLogout, companyName, logoUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const isActive = (path) => location.pathname === path;

  // Split company name for the logo
  const companyParts = companyName ? companyName.split(' ') : ['BSCC', 'Infrastructure'];
  const firstPart = companyParts[0];
  const restPart = companyParts.slice(1).join(' ');

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
    { path: '/rigs', label: 'Rigs', icon: Factory, testId: 'nav-rigs' },
    { path: '/employees', label: 'Employees', icon: Users, testId: 'nav-employees' },
    { path: '/attendance', label: 'Attendance', icon: ClipboardCheck, testId: 'nav-attendance' },
    { path: '/payroll', label: 'Payroll', icon: FileText, testId: 'nav-payroll' },
    { path: '/settings', label: 'Settings', icon: SettingsIcon, testId: 'nav-settings' },
  ];

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <Link to="/" className="flex-shrink-0 flex items-center group">
              {logoUrl ? (
                <div className="w-10 h-10 rounded-lg overflow-hidden mr-3 border border-gray-100 bg-white flex items-center justify-center group-hover:shadow-md transition-all duration-200">
                  <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-700 transition-all duration-200">
                  <span className="text-white font-bold text-xl">{firstPart.charAt(0)}</span>
                </div>
              )}
              <div className="transition-transform group-hover:translate-x-0.5 duration-200">
                <h1 className="text-gray-900 text-sm sm:text-lg font-bold leading-tight uppercase">{firstPart}</h1>
                <p className="text-gray-500 text-[9px] sm:text-[10px] uppercase tracking-wider font-semibold">{restPart || 'Infrastructure'}</p>
              </div>
            </Link>

            <div className="hidden md:ml-10 md:flex md:space-x-4 md:items-center">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={item.testId}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(item.path)
                    ? 'bg-blue-50 text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600'
                    }`}
                >
                  <item.icon size={18} className="mr-2" />
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center px-3 py-1 bg-gray-50 rounded-full border border-gray-100">
              <div className="text-right mr-3">
                <p className="text-gray-900 text-xs font-bold">{user.full_name}</p>
                <p className="text-gray-500 text-[10px] uppercase tracking-tighter">{user.role}</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                {user.full_name?.charAt(0)}
              </div>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-screen border-b border-gray-200' : 'max-h-0'}`}>
        <div className="px-2 pt-2 pb-3 space-y-1 bg-white">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={item.testId}
              onClick={() => setIsOpen(false)}
              className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-colors ${isActive(item.path)
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600'
                }`}
            >
              <item.icon size={20} className="mr-3" />
              {item.label}
            </Link>
          ))}
          <div className="pt-4 pb-3 border-t border-gray-100 mt-2">
            <div className="flex items-center px-4 mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                  {user.full_name?.charAt(0)}
                </div>
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800">{user.full_name}</div>
                <div className="text-sm font-medium text-gray-500 uppercase tracking-wider">{user.role}</div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex w-full items-center px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={20} className="mr-3" />
              Logout from System
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  // Read from localStorage synchronously — paints LCP element on first commit without waiting for API
  const [companyName, setCompanyName] = useState(
    () => localStorage.getItem('company_name') || 'BSCC Infrastructure'
  );
  const [logoUrl, setLogoUrl] = useState(() => {
    const cached = localStorage.getItem('company_logo');
    return cached && cached !== 'null' ? cached : null;
  });

  useEffect(() => {
    checkAuth();
    fetchCompanyName();
    fetchLogo();

    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

    const checkSessionTimeout = () => {
      const token = localStorage.getItem('token');
      if (!token) return false;

      const lastActive = localStorage.getItem('lastActiveTime');
      if (lastActive && Date.now() - parseInt(lastActive) > INACTIVITY_TIMEOUT) {
        handleLogout(false);
        return true;
      }
      return false;
    };

    // Initial check handled by checkAuth called above

    const updateLastActive = () => {
      if (localStorage.getItem('token')) {
        localStorage.setItem('lastActiveTime', Date.now().toString());
      }
    };

    // Listen for activity only if authenticated
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    if (isAuthenticated) {
      activityEvents.forEach(event => {
        window.addEventListener(event, updateLastActive);
      });
    }

    // Periodically check for timeout (every minute)
    let timeoutInterval;
    if (isAuthenticated) {
      timeoutInterval = setInterval(() => {
        if (checkSessionTimeout()) {
          // Timeout occurred
        }
      }, 60000);
    }

    // Listen for logo and company name updates from Settings
    window.addEventListener('logo-updated', fetchLogo);
    window.addEventListener('company-name-updated', fetchCompanyName);

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateLastActive);
      });
      clearInterval(timeoutInterval);
      window.removeEventListener('logo-updated', fetchLogo);
      window.removeEventListener('company-name-updated', fetchCompanyName);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const fetchLogo = async () => {
    try {
      // State already initialized from localStorage — just refresh from API in background
      const cachedLogo = localStorage.getItem('company_logo');
      const response = await axiosWithRetry({ method: 'get', url: `${API}/settings/logo-status` });
      const newLogoUrl = response.data.exists ? response.data.url : null;
      
      if (newLogoUrl !== (cachedLogo === 'null' ? null : cachedLogo)) {
        setLogoUrl(newLogoUrl);
        localStorage.setItem('company_logo', newLogoUrl || 'null');
      }
    } catch (error) {
      console.warn('Could not refresh logo after retries:', error?.message);
    }
  };

  const fetchCompanyName = async () => {
    try {
      // State already initialized from localStorage — just refresh from API in background
      const response = await axiosWithRetry({ method: 'get', url: `${API}/settings/company-name` });
      const newName = response.data.company_name || 'BSCC Infrastructure';
      
      if (newName !== companyName) { // Compare with current state
        setCompanyName(newName);
        localStorage.setItem('company_name', newName);
      }
    } catch (error) {
      console.warn('Could not refresh company name after retries:', error?.message);
    }
  };

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    if (token) {
      const lastActive = localStorage.getItem('lastActiveTime');
      const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
      if (lastActive && Date.now() - parseInt(lastActive) > INACTIVITY_TIMEOUT) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('lastActiveTime');
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
    }

    setIsAuthenticated(!!token);

    // Check if user needs to change password on first login
    if (token && user.first_login) {
      setIsFirstLogin(true);
      setShowChangePassword(true);
    }

    setLoading(false);
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    localStorage.setItem('lastActiveTime', Date.now().toString());
    toast.success('Login Successful');

    // Check if this is first login
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.first_login) {
      setIsFirstLogin(true);
      setShowChangePassword(true);
      toast.info('Please change your password to continue');
    }
  };

  const handlePasswordChanged = () => {
    // Update user data in localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    user.first_login = false;
    localStorage.setItem('user', JSON.stringify(user));

    setIsFirstLogin(false);
    setShowChangePassword(false);
  };

  const handleLogout = (showToast = true) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('lastActiveTime');
    setIsAuthenticated(false);
    setShowChangePassword(false);
    setIsFirstLogin(false);
    if (showToast) {
      toast.success('Logged out successfully');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-gray-50">
      {!isAuthenticated ? (
        <Login onLoginSuccess={handleLoginSuccess} />
      ) : (
        <BrowserRouter>
          <Navigation onLogout={handleLogout} companyName={companyName} logoUrl={logoUrl} />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<PrivateRoute><Dashboard companyName={companyName} logoUrl={logoUrl} /></PrivateRoute>} />
              <Route path="/rigs" element={<PrivateRoute><Rigs /></PrivateRoute>} />
              <Route path="/employees" element={<PrivateRoute><Employees /></PrivateRoute>} />
              <Route path="/attendance" element={<PrivateRoute><Attendance /></PrivateRoute>} />
              <Route path="/payroll" element={<PrivateRoute><Payroll /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
            </Routes>
          </Suspense>
          </div>
        </BrowserRouter>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal
          isFirstLogin={isFirstLogin}
          onPasswordChanged={handlePasswordChanged}
          onClose={isFirstLogin ? null : () => setShowChangePassword(false)}
        />
      )}

      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          style: {
            padding: '12px 16px',
            paddingRight: '32px', // Extra space for close button
          },
          classNames: {
            toast: 'rounded-xl shadow-lg border-gray-100',
            title: 'font-bold uppercase tracking-tight text-xs',
            description: 'text-xs font-medium',
          }
        }}
      />
    </div>
  );
}

export default App;
